import { useQuery } from '@tanstack/react-query';

import { Query } from './api';
import { getPluralEntity } from '../utils/entities';
import { QueryParams } from '../types/api.types';

export const useEntities = <T = any>(
  accountId: string,
  entityType: string,
  options?: QueryParams,
  queryParams?: QueryParams,
) => {
  const params = options || {};

  let pluralEntity = getPluralEntity(entityType)('entityHook');

  if (!pluralEntity && options?.useExplicitEntityType !== true) {
    console.error(`No plural entity found for type: ${entityType}`);
  }

  if (options?.useExplicitEntityType) {
    pluralEntity = entityType;
    delete params.useExplicitEntityType;
  }

  return useQuery({
    queryKey: [accountId, pluralEntity, params],

    queryFn: async () => {
      let query = new Query(`accounts/${accountId}/${pluralEntity}`)
        .page(params.page || 1)
        .size(params.size || 10)
        .include(...(params.include || []))
        .filter(params.filter);

      if (params.sort) {
        params.sort.forEach((field) => {
          query = query.sort_by(
            field.field,
            field.sort === 'desc' ? true : false,
          );
        });
      }

      return await query.run<T>();
    },

    ...queryParams,
    staleTime: 300000,
  });
};

export const useEntity = <T = any>(
  accountId?: string,
  entityType?: string,
  entityId?: string,
  options?: QueryParams,
) => {
  const params = options || {};
  const pluralEntity = getPluralEntity(entityType)('entityHook');

  if (!pluralEntity) {
    console.error(`No plural entity found for type: ${entityType}`);
  }

  return useQuery({
    queryKey: [accountId, pluralEntity, entityId],

    queryFn: async () => {
      if (!entityId) return null;

      return await new Query(
        `accounts/${accountId}/${pluralEntity}/${entityId}`,
      )
        .include(...(params.include || []))
        .filter(params.filter || {})
        .run<T>();
    },

    ...params,
    staleTime: 300000,
  });
};
